/**
 * Shared Types Index
 * Central export point for all shared types
 */

// NOTE: environment.d.ts and css-modules.d.ts are ambient declarations
// that augment global types. They don't need to be exported - TypeScript
// automatically includes them when they're in the tsconfig include path.

// Generic API Types
export type { ApiError, ApiResponse } from './api';
export { isApiError } from './api';

// Client Types
export type {
  ClientResponse,
  GetClientsResponse,
  ClientUserResponse,
  GetClientUsersResponse,
  ClientCategoryResponse,
  GetClientCategoriesResponse,
  // Client Details (Enriched)
  ClientAccountInfo,
  ClientEntAuth,
  ClientPackage,
  ClientTranscription,
  ClientWebhookAuth,
  ClientWebhook,
  ClientTech,
  ClientVendor,
  ClientApiKeys,
  ClientDetailsResponse,
  GetClientDetailsResponse,
  ClientServiceBasis,
  ClientService,
  ClientPlatformSummary,
  GetClientPlatformSummaryResponse,
} from './clients';

// Event & Stream Types
export type {
  EventDetailsResponse,
  EventFullDetailsResponse,
  CaptionPipelineData,
  SimulcastTarget,
  LiveStreamResponse,
  LiveStreamWithEventResponse,
  LiveStreamTableRow,
  GetLiveStreamsResponse,
  UpcomingStreamResponse,
  CacheInfo,
  GetUpcomingStreamsResponse,
} from './events';

export { EVENT_STATUS_NAMES, getEventStatusName } from './events';

// Encoder Types
export type {
  PersistentStreamResponse,
  GetPersistentStreamsResponse,
  ClientEncoderStreamInfoResponse,
  ClientEncoderResponse,
  GetClientEncodersResponse,
} from './encoders';

export { ENCODER_CONFIG } from './encoders';

// Probe Types
export type {
  VideoMetadata,
  AudioMetadata,
  MediaMetadata,
  ProbeResult,
  ProbeRequestOptions,
  ProbeRequest,
  // Deep probe types (VOD - MediaInfo)
  BitrateMode,
  BitrateAnalysis,
  GopAnalysis,
  DeepProbeResult,
  // Stream deep probe types (live RTMP - ffprobe)
  StreamBitrateAnalysis,
  StreamGopAnalysis,
  StreamDeepProbeResult,
} from './probe';

// Reports & Trends Types
export type {
  ReportsQueryParams,
  MonthlyLiveStreamData,
  MonthlyVODData,
  ReportsResponse,
  EncoderStreamInfo,
  EncoderLiveStreamData,
  EncoderVODData,
  ByEncoderReportsResponse,
  AllClientsEventsQueryParams,
  ClientEventsSummary,
  AllClientsReportsResponse,
  TrendsQueryParams,
  TrendDataPoint,
  TrendsResponse,
} from './reports';

// Simulcast Reports Types
export type {
  SimulcastQueryParams,
  MonthlySimulcastData,
  SimulcastTotals,
  SimulcastReportResponse,
  ClientSimulcastSummary,
  AllClientsSimulcastResponse,
} from './simulcast';

// Config Types
export type { ConfigItem, ConfigGroup } from './config';

// WebSocket Types
export type {
  WebSocketEventData,
  ScaledroneMessage,
  ConnectionStatus,
  ScaledroneWorkerMessage,
  MainToWorkerMessage,
  EncoderWSSData,
  EncoderWSSEventType,
  EncoderWSSEvent,
} from './websocket';

export {
  LIVE_EVENT_STATUSES,
  COMPLETED_EVENT_STATUS,
  NEW_EVENT_STATUS,
  isLiveEventStatus,
  isCompletedEventStatus,
  isEventMessage,
} from './websocket';

// Auth Types
export * from './auth';

// Triage/SSE Types
export type {
  EncoderSampleData,
  TriageSession,
  TriageSample,
  SSEEventType,
  SSEEvent,
  SSEConnectedPayload,
  SSEHeartbeatPayload,
  EncoderAlertType,
  AlertSeverity,
  EncoderAlertPayload,
  EncoderUpdatePayload,
  TriageStartedPayload,
  TriageEndedPayload,
  StartTriageRequest,
  StartTriageResponse,
  GetTriageSessionResponse,
  GetAllTriageSessionsResponse,
  GetTriageHistoryResponse,
  EndTriageResponse,
  SSEStatsResponse,
  AlertHistoryRecord,
  AlertHistorySummary,
} from './triage';

export { TRIAGE_CONFIG, ANOMALY_CONFIG, ALERT_HISTORY_CONFIG } from './triage';

// Poller Types
export type {
  WorkerToMainMessage,
  PollerConfig,
  PollerStats,
  PollCycleStats,
  RingBufferEntry,
  RingBufferStats,
} from './poller';

export { POLLER_CONFIG } from './poller';

// Transcription & Caption Reports Types
export type {
  TranscriptionReportsQueryParams,
  TranscriptionRateConfig,
  MonthlyTranscriptionData,
  TranscriptionUsageResponse,
  MonthlyLiveCaptionData,
  LiveCaptionUsageResponse,
  // All-Clients Report Types
  AllClientsReportsQueryParams,
  AllClientsTranscriptionClient,
  AllClientsTranscriptionResponse,
  AllClientsCaptionClient,
  AllClientsCaptionResponse,
} from './transcription';

// Transcode Job Types
export type {
  TranscodeJob,
  StallStatus,
  TrackedJob,
  TranscodeJobFilters,
  TranscodeJobsMeta,
  TranscodeJobsResponse,
  TranscodeActionType,
  TranscodeWSSMessage,
  StallDetectionConfig,
} from './transcode';

export {
  TRANSCODE_CONFIG,
  DEFAULT_STALL_CONFIG,
  detectStallStatus,
  createTrackedJob,
} from './transcode';

// VOD Diagnostics Types
export type {
  CheckStatus,
  CheckSeverity,
  OverallStatus,
  Check,
  StoredMetadata,
  AssetDiagnosis,
  DiagnosisSummary,
  DiagnoseResponse,
} from './vod-diagnostics';

// Flapping Types
export type {
  FlappingSeverity,
  FlappingEventRecord,
  FlappingAlertPayload,
  FlappingResolvedPayload,
  FlappingHistoryRecord,
  GetFlappingResponse,
} from './flapping';

export { FLAPPING_CONFIG, isDuplicateEvent } from './flapping';

// VTT Analysis Types
export type {
  VttSegment,
  VttGap,
  VttConsecutiveRun,
  VttMarkerStats,
  VttRepeatedPhrase,
  VttRepetitionAnalysis,
  VttLongLine,
  VttQualityAnalysis,
  VttAnalysisResult,
  VttAnalyzeRequest,
  // Hallucination detection
  VttWordRepetition,
  VttDuplicateText,
  VttHallucinationPhrase,
  VttHallucinationAnalysis,
} from './vtt';

export { VTT_LONG_LINE_THRESHOLD, VTT_WORD_REPETITION_THRESHOLD } from './vtt';

// OTT Manager Types
export type {
  AppSubmissionStatus,
  AppSubmissionStatusRecord,
  OTTSubmission,
  OTTStoreTarget,
  OTTApp,
  OTTClient,
  GetOTTManagerResponse,
  // Legacy flat types (deprecated)
  OTTAppSubmission,
  GetOTTAppSubmissionsResponse,
} from './ott-manager';

// Analytics Types
export type {
  PlayerStatsEventViewerCount,
  PlayerStatsEventsViewerCountsResponse,
  EventViewerStats,
  ViewerCountsByEvent,
  GetViewerCountsResponse,
  ViewerCountsQueryParams,
} from './analytics';

// Caption Job Monitoring Types
export type {
  CaptionJobStatus,
  CaptionJobsQueryParams,
  CaptionJobRow,
  CaptionJobsResponse,
  VttQualitySeverity,
  VttQualityScore,
  VttBatchAnalyzeRequest,
  VttBatchAnalyzeResponse,
} from './captionJobs';

export { LEAC_ENDED_STATUSES, LEAC_ACTIVE_STATUSES, CAPTION_JOBS_CONFIG } from './captionJobs';

// Pipeline Message Log Types
export type {
  PipelineSearchType,
  PipelineLogType,
  PipelineCategory,
  PipelineLogQueryParams,
  PipelineLogExtracted,
  PipelineLogEntry,
  PipelineLogsResponse,
} from './pipeline';

export { PIPELINE_CONFIG } from './pipeline';

// Webhook Log Monitoring Types
export type {
  WebhookLogStatus,
  WebhookLogRangeLabel,
  WebhookLogsQueryParams,
  WebhookLogRow,
  WebhookLogDetail,
  WebhookLogsResponse,
  WebhookLogDetailResponse,
} from './webhookLogs';

export { WEBHOOK_LOGS_CONFIG, WEBHOOK_LOG_RANGES } from './webhookLogs';
